"use client";

import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Edit2,
  Loader2,
  Package,
  Plus,
  RefreshCw,
  Search,
  Server,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { fetchApp } from "../../services/apiService";

type IdValue = string | number;

interface Plan {
  id: IdValue;
  name: string;
  mikrotik_profile_id?: string | null;
  local_address?: string | null;
  remote_pool?: string | null;
  speed: string;
  dns_servers?: string | null;
  description?: string | null;
  price: number;
  created_at: string;
  updated_at: string;
  router_id?: IdValue | null;
  router_name?: string | null;
  router_auth_method?: string | null;
  is_radius?: boolean;
}

interface RouterInfo {
  id?: IdValue | null;
  name: string;
  ip_address?: string | null;
  auth_method?: string | null;
  mikrotik_username?: string | null;
  mikrotik_password?: string | null;
  wg_private_key?: string | null;
}

interface UserRole {
  id: IdValue;
  name: string;
}

interface CurrentUser {
  id: IdValue;
  username: string;
  email: string;
  client_id: IdValue;
  role_id: IdValue;
  role: UserRole;
  routers?: RouterInfo[];
}

interface RouterOption {
  id: IdValue;
  name: string;
  auth_method?: string | null;
}

interface PlanFormData {
  router_id: string;
  name: string;
  mikrotik_profile_id: string;
  local_address: string;
  remote_pool: string;
  speed: string;
  dns_servers: string;
  description: string;
  price: string;
}

interface Secret {
  id: IdValue;
  username: string;
  status: boolean;
  account_number: IdValue;
  phone_number: string;
  email: string;
  created_at: string;
}

interface PaginatedSecrets {
  items: Secret[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const emptyForm: PlanFormData = {
  router_id: "",
  name: "",
  mikrotik_profile_id: "",
  local_address: "",
  remote_pool: "",
  speed: "",
  dns_servers: "",
  description: "",
  price: "",
};

const fmtKES = (value: number) =>
  `KES ${new Intl.NumberFormat("en-KE").format(Number(value || 0))}`;

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return "—";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return iso;

  return d.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeAuthMethod = (value?: string | null) =>
  String(value || "api")
    .trim()
    .toLowerCase();

const planAuthMethod = (plan?: Plan | null) => {
  if (!plan) return "api";

  if (plan.is_radius) return "radius";

  return normalizeAuthMethod(plan.router_auth_method);
};

const isRadiusAuth = (authMethod?: string | null) =>
  normalizeAuthMethod(authMethod) === "radius";

const readStoredCurrentUser = (): CurrentUser | null => {
  try {
    const raw = localStorage.getItem("user");

    if (!raw) return null;

    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
};

const mapRoutersFromCurrentUser = (currentUser?: CurrentUser | null) => {
  const userRouters = Array.isArray(currentUser?.routers)
    ? currentUser.routers
    : [];

  return userRouters
    .filter((router) => router?.id)
    .map((router) => ({
      id: router.id as IdValue,
      name: router.name || "Unnamed Router",
      auth_method: normalizeAuthMethod(router.auth_method),
    }));
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "min-h-[92px] w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-2.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const getInitials = (name: string) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .map((item) => item[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

const AVATAR_PALETTES = [
  "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  "bg-sky-100 text-sky-700 ring-1 ring-sky-200",
  "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
  "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
  "bg-teal-100 text-teal-700 ring-1 ring-teal-200",
  "bg-rose-100 text-rose-700 ring-1 ring-rose-200",
];

const avatarColor = (name: string) =>
  AVATAR_PALETTES[
    (name || "").split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      AVATAR_PALETTES.length
  ];

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  hint?: React.ReactNode;
  span2?: boolean;
}> = ({ label, required, children, hint, span2 }) => (
  <div
    className={["flex flex-col gap-1.5", span2 ? "sm:col-span-2" : ""]
      .filter(Boolean)
      .join(" ")}
  >
    <label className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>

    {children}

    {hint && (
      <div className="text-[11.5px] font-medium leading-snug text-slate-500">
        {hint}
      </div>
    )}
  </div>
);

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
      active
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-slate-300 bg-slate-200 text-slate-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-slate-500"
      }`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-3xl font-black tracking-tight text-slate-900">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [routers, setRouters] = useState<RouterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRouter, setSelectedRouter] = useState<string>("all");

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const [planUsers, setPlanUsers] = useState<Secret[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRoutersFromCurrentUser = () => {
    const currentUser = readStoredCurrentUser();
    const mappedRouters = mapRoutersFromCurrentUser(currentUser);

    setRouters(mappedRouters);

    if (currentUser?.routers?.length && mappedRouters.length === 0) {
      setError(
        "Routers were found on the logged-in user, but router id is missing. Add id and auth_method to RouterInfo from /auth/me.",
      );
    }
  };

  const fetchPlans = async (keepSelection = true) => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchApp<Plan[]>("/plans");
      const list = Array.isArray(data) ? data : [];

      setPlans(list);

      if (keepSelection && selectedPlan) {
        const fresh =
          list.find((p) => String(p.id) === String(selectedPlan.id)) || null;
        setSelectedPlan(fresh);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutersFromCurrentUser();
    fetchPlans(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueRouters = useMemo<RouterOption[]>(() => {
    if (routers.length > 0) return routers;

    const routerMap = new Map<string, RouterOption>();

    plans.forEach((plan) => {
      if (plan.router_id && plan.router_name) {
        routerMap.set(String(plan.router_id), {
          id: plan.router_id,
          name: plan.router_name,
          auth_method: planAuthMethod(plan),
        });
      }
    });

    return Array.from(routerMap.values());
  }, [plans, routers]);

  const selectedFormRouter = useMemo(() => {
    if (!formData.router_id) return null;

    return (
      uniqueRouters.find(
        (routerOption) =>
          String(routerOption.id) === String(formData.router_id),
      ) || null
    );
  }, [formData.router_id, uniqueRouters]);

  const formAuthMethod = editingPlan
    ? planAuthMethod(editingPlan)
    : normalizeAuthMethod(selectedFormRouter?.auth_method);

  const radiusPlanForm = isRadiusAuth(formAuthMethod);

  const filteredPlans = useMemo(() => {
    let filtered = plans;

    if (selectedRouter !== "all") {
      filtered = filtered.filter(
        (plan) => String(plan.router_id || "") === selectedRouter,
      );
    }

    const q = searchQuery.trim().toLowerCase();

    if (q) {
      filtered = filtered.filter((plan) => {
        const name = (plan.name || "").toLowerCase();
        const speed = (plan.speed || "").toLowerCase();
        const desc = (plan.description || "").toLowerCase();
        const router = (plan.router_name || "").toLowerCase();

        return (
          name.includes(q) ||
          speed.includes(q) ||
          desc.includes(q) ||
          router.includes(q)
        );
      });
    }

    return filtered;
  }, [plans, searchQuery, selectedRouter]);

  const stats = useMemo(() => {
    const avgPrice =
      plans.length > 0
        ? Math.round(
            plans.reduce((sum, plan) => sum + plan.price, 0) / plans.length,
          )
        : 0;

    return {
      total: plans.length,
      avgPrice,
      filtered: filteredPlans.length,
    };
  }, [plans, filteredPlans]);

  const fetchPlanUsers = async (planId: IdValue, page = 1) => {
    try {
      setUsersLoading(true);
      setError(null);

      const data = await fetchApp<PaginatedSecrets>(
        `/plans/${planId}/users?page=${page}&page_size=10`,
      );

      setPlanUsers(data.items || []);
      setUsersPage(data.page || 1);
      setUsersTotalPages(data.total_pages || 1);
    } catch (err: any) {
      setError(err?.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const openCreate = () => {
    const defaultRouterId =
      selectedRouter !== "all"
        ? selectedRouter
        : uniqueRouters.length > 0
          ? String(uniqueRouters[0].id)
          : "";

    setEditingPlan(null);
    setFormData({
      ...emptyForm,
      router_id: defaultRouterId,
    });
    setDrawerOpen(true);
    setError(null);
    setSuccess(null);
  };

  const openEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      router_id: plan.router_id ? String(plan.router_id) : "",
      name: plan.name || "",
      mikrotik_profile_id: plan.mikrotik_profile_id || "",
      local_address: plan.local_address || "",
      remote_pool: plan.remote_pool || "",
      speed: plan.speed || "",
      dns_servers: plan.dns_servers || "",
      description: plan.description || "",
      price: String(plan.price ?? ""),
    });
    setDrawerOpen(true);
    setError(null);
    setSuccess(null);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingPlan(null);
    setFormData(emptyForm);
    setSaving(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      const syncPath =
        selectedRouter !== "all"
          ? `/plans/sync?router_id=${encodeURIComponent(selectedRouter)}`
          : "/plans/sync";

      await fetchApp(syncPath, { method: "POST" });
      setSuccess("Sync request completed.");
      await fetchPlans(true);

      if (selectedPlan) {
        await fetchPlanUsers(selectedPlan.id, 1);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to sync plans");
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError("Plan name is required");
      return;
    }

    if (!formData.speed.trim()) {
      setError("Speed / rate-limit is required");
      return;
    }

    if (
      formData.price === "" ||
      Number.isNaN(Number.parseInt(formData.price, 10))
    ) {
      setError("Valid price is required");
      return;
    }

    if (!editingPlan && !formData.router_id) {
      setError("Please select a router for this plan");
      return;
    }

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      speed: formData.speed.trim(),
      description: formData.description.trim() || null,
      price: Number.parseInt(formData.price, 10),
    };

    if (!editingPlan) {
      payload.router_id = formData.router_id;
    }

    if (!radiusPlanForm) {
      payload.mikrotik_profile_id = formData.mikrotik_profile_id.trim() || null;
      payload.local_address = formData.local_address.trim() || null;
      payload.remote_pool = formData.remote_pool.trim() || null;
      payload.dns_servers = formData.dns_servers.trim() || null;
    }

    setSaving(true);

    try {
      if (editingPlan) {
        await fetchApp(`/plans/${editingPlan.id}/update-and-sync`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        setSuccess(
          radiusPlanForm
            ? "Radius plan updated in database."
            : "Plan updated and synced.",
        );
      } else {
        await fetchApp("/plans/add-and-sync", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        setSuccess(
          radiusPlanForm
            ? "Radius plan created in database."
            : "Plan created and synced.",
        );
      }

      await fetchPlans(true);

      if (selectedPlan && editingPlan && selectedPlan.id === editingPlan.id) {
        await fetchPlanUsers(selectedPlan.id, usersPage);
      }

      setTimeout(() => closeDrawer(), 350);
    } catch (err: any) {
      setError(err?.message || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    setSelectedPlan(plan);
    setPlanUsers([]);
    setUsersPage(1);
    setUsersTotalPages(1);
    await fetchPlanUsers(plan.id, 1);
  };

  const handleUsersPageChange = (dir: "prev" | "next") => {
    if (!selectedPlan) return;

    const next =
      dir === "prev"
        ? Math.max(1, usersPage - 1)
        : Math.min(usersTotalPages, usersPage + 1);

    if (next !== usersPage) {
      fetchPlanUsers(selectedPlan.id, next);
    }
  };

  return (
    <div className="min-h-screen bg-[#eef3f8] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Package Management
                </div>

                <h1 className="mt-3 text-[24px] font-black tracking-[-0.5px] text-slate-900">
                  Internet Plans
                </h1>

                <p className="mt-1 max-w-2xl text-[13.5px] font-medium text-slate-500">
                  Manage packages, pricing, speed limits, and MikroTik PPP
                  profile synchronisation.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={syncing}
                  className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {syncing ? "Syncing…" : "Sync MikroTik"}
                </button>

                <button
                  type="button"
                  onClick={openCreate}
                  className={`${btnBase} bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  New Plan
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {(error || success) && (
          <div className="space-y-3">
            {error && (
              <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{error}</p>
                </div>

                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {success && (
              <div className="flex items-start justify-between gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
                <div className="flex items-start gap-3">
                  <Activity className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{success}</p>
                </div>

                <button
                  type="button"
                  onClick={() => setSuccess(null)}
                  className="rounded-xl p-1.5 text-emerald-700 transition-colors hover:bg-emerald-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total Plans"
            value={stats.total}
            sub="Available packages"
            icon={<Package className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Average Price"
            value={stats.avgPrice ? fmtKES(stats.avgPrice) : "—"}
            sub="Average package cost"
            icon={<DollarSign className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Showing"
            value={stats.filtered}
            sub="Current filtered result"
            icon={<TrendingUp className="h-5 w-5" />}
            tone="sky"
          />
        </div>

        {/* Toolbar */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50/95 p-4 shadow-lg shadow-slate-300/25">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="relative md:w-64">
              <Server className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <select
                value={selectedRouter}
                onChange={(e) => setSelectedRouter(e.target.value)}
                className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
              >
                <option value="all">All Routers</option>

                {uniqueRouters.length === 0 && (
                  <option value="" disabled>
                    No routers found
                  </option>
                )}

                {uniqueRouters.map((router) => (
                  <option key={router.id} value={router.id}>
                    {router.name}
                  </option>
                ))}
              </select>

              <svg
                className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, speed, router, or description…"
                className={`${inputCls} pl-10`}
              />
            </div>

            {(selectedRouter !== "all" || searchQuery) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedRouter("all");
                  setSearchQuery("");
                }}
                className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                <X className="h-4 w-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Plans list */}
          <div className="space-y-4 lg:col-span-5">
            <div className="flex items-center justify-between">
              <h2 className="text-[15px] font-black text-slate-900">
                Available Plans
              </h2>

              <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-[11.5px] font-black text-slate-600">
                {filteredPlans.length}{" "}
                {filteredPlans.length === 1 ? "Plan" : "Plans"}
              </span>
            </div>

            {loading ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
                <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
                  <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
                </div>

                <p className="mt-3 text-[13px] font-bold text-slate-500">
                  Loading plans…
                </p>
              </div>
            ) : filteredPlans.length === 0 ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-12 text-center shadow-xl shadow-slate-300/30">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
                  <Package className="h-8 w-8 text-slate-500" />
                </div>

                <p className="text-[15px] font-black text-slate-800">
                  {searchQuery ? "No matching plans" : "No plans yet"}
                </p>

                <p className="mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  {searchQuery
                    ? "Try adjusting your search terms or filters."
                    : "Create your first internet plan to get started."}
                </p>

                {!searchQuery && (
                  <button
                    type="button"
                    onClick={openCreate}
                    className={`${btnBase} mt-5 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                  >
                    <Plus className="h-4 w-4" />
                    Create Plan
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPlans.map((plan) => {
                  const active = selectedPlan?.id === plan.id;

                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => handleSelectPlan(plan)}
                      className={`group w-full overflow-hidden rounded-3xl border text-left shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl ${
                        active
                          ? "border-emerald-300 bg-emerald-50/95 shadow-emerald-200/40"
                          : "border-slate-200 bg-slate-50/95 shadow-slate-300/25 hover:border-slate-300"
                      }`}
                    >
                      <div
                        className={`h-1 w-full ${
                          active
                            ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                            : "bg-gradient-to-r from-slate-300 to-slate-400"
                        }`}
                      />

                      <div className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 flex items-center gap-2">
                              <h3 className="truncate text-[15px] font-black text-slate-900">
                                {plan.name}
                              </h3>

                              {active && (
                                <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                              )}
                            </div>

                            <div className="mb-3 flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                                <Zap className="h-3.5 w-3.5" />
                                {plan.speed || "—"}
                              </span>

                              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
                                {fmtKES(plan.price)}
                              </span>

                              {plan.router_name && (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                                  <Server className="h-3.5 w-3.5" />
                                  {plan.router_name}
                                </span>
                              )}
                            </div>

                            {plan.description && (
                              <p className="line-clamp-2 text-[12.5px] font-medium leading-relaxed text-slate-500">
                                {plan.description}
                              </p>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(plan);
                            }}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-300 bg-slate-50 text-slate-500 opacity-100 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800 sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
                          <p className="text-[11px] font-medium text-slate-400">
                            Updated {fmtDateTime(plan.updated_at)}
                          </p>

                          <ArrowUpRight className="h-4 w-4 text-slate-400 transition-colors group-hover:text-emerald-500" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plan details */}
          <div className="lg:col-span-7">
            {!selectedPlan ? (
              <div className="flex min-h-[460px] flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50/95 px-4 py-12 text-center shadow-xl shadow-slate-300/30">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-200">
                  <Users className="h-8 w-8 text-slate-500" />
                </div>

                <p className="text-[15px] font-black text-slate-800">
                  Select a plan
                </p>

                <p className="mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Click any plan to view profile details and assigned PPPoE
                  users.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white shadow-xl shadow-slate-400/30">
                  <div className="h-1.5 w-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />

                  <div className="relative overflow-hidden p-5 sm:p-6">
                    <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-emerald-400/15 blur-2xl" />
                    <div className="absolute -bottom-20 left-1/3 h-44 w-44 rounded-full bg-cyan-400/15 blur-2xl" />

                    <div className="relative">
                      <div className="mb-4 flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 text-[11px] font-black uppercase tracking-widest text-emerald-300">
                            Selected Plan
                          </p>

                          <h2 className="truncate text-[23px] font-black tracking-[-0.4px] text-white">
                            {selectedPlan.name}
                          </h2>

                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-2xl border border-white/10 bg-white/10 px-3 py-1.5 text-[12px] font-bold text-white backdrop-blur-sm">
                              <Zap className="h-4 w-4" />
                              {selectedPlan.speed}
                            </span>

                            <span className="inline-flex items-center gap-1.5 rounded-2xl border border-emerald-400/30 bg-emerald-500/20 px-3 py-1.5 text-[12px] font-black text-emerald-100 backdrop-blur-sm">
                              <DollarSign className="h-4 w-4" />
                              {fmtKES(selectedPlan.price)} / month
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => openEdit(selectedPlan)}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition-colors hover:bg-white/20"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      </div>

                      {selectedPlan.description && (
                        <p className="mb-4 text-[13px] font-medium leading-relaxed text-slate-200">
                          {selectedPlan.description}
                        </p>
                      )}

                      {isRadiusAuth(planAuthMethod(selectedPlan)) ? (
                        <div className="rounded-3xl border border-violet-300/20 bg-violet-400/10 p-4 backdrop-blur-sm">
                          <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-violet-200">
                            Radius Plan
                          </p>

                          <p className="text-[13px] font-medium leading-relaxed text-violet-50">
                            This plan belongs to a Radius router. MikroTik API
                            profile fields are hidden because billing and
                            rate-limit data are stored in the database only.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                              <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-300">
                                MikroTik Profile
                              </p>

                              <p className="break-all font-mono text-[13px] font-bold text-white">
                                {selectedPlan.mikrotik_profile_id || "—"}
                              </p>
                            </div>

                            <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                              <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-300">
                                Network
                              </p>

                              <p className="text-[13px] font-medium text-white">
                                Local: {selectedPlan.local_address || "—"}
                                <br />
                                Pool: {selectedPlan.remote_pool || "—"}
                              </p>
                            </div>
                          </div>

                          {selectedPlan.dns_servers && (
                            <div className="mt-3 rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                              <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-300">
                                DNS Servers
                              </p>

                              <p className="font-mono text-[13px] font-bold text-white">
                                {selectedPlan.dns_servers}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-100/80 px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                        <Users className="h-5 w-5" />
                      </div>

                      <div>
                        <h3 className="text-[14px] font-black text-slate-900">
                          Assigned Users
                        </h3>

                        <p className="text-[11.5px] font-medium text-slate-500">
                          PPPoE customers currently using this plan
                        </p>
                      </div>
                    </div>

                    <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1 text-[11px] font-black text-slate-600">
                      Page {usersPage} of {usersTotalPages}
                    </span>
                  </div>

                  {usersLoading ? (
                    <div className="flex min-h-[260px] flex-col items-center justify-center px-5 py-16">
                      <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
                        <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
                      </div>

                      <p className="mt-3 text-[13px] font-bold text-slate-500">
                        Loading users…
                      </p>
                    </div>
                  ) : planUsers.length === 0 ? (
                    <div className="px-5 py-14 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Users className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="mb-1 text-[14px] font-black text-slate-800">
                        No users assigned
                      </p>

                      <p className="text-[13px] font-medium text-slate-500">
                        Users with this plan will appear here.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="hidden overflow-x-auto md:block">
                        <table className="min-w-full text-left">
                          <thead>
                            <tr className="border-b border-slate-200 bg-slate-100">
                              {["Customer", "Status", "Account", "Contact"].map(
                                (header) => (
                                  <th
                                    key={header}
                                    className="px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-slate-500"
                                  >
                                    {header}
                                  </th>
                                ),
                              )}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                            {planUsers.map((user) => {
                              const initials = getInitials(user.username);
                              const palette = avatarColor(user.username);

                              return (
                                <tr
                                  key={user.id}
                                  className="transition-colors hover:bg-emerald-50/60"
                                >
                                  <td className="px-5 py-4">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-black ${palette}`}
                                      >
                                        {initials}
                                      </div>

                                      <div className="min-w-0">
                                        <p className="truncate text-[13px] font-black text-slate-900">
                                          {user.username}
                                        </p>

                                        <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                                          {user.email || "—"}
                                        </p>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-5 py-4">
                                    <StatusBadge
                                      active={Boolean(user.status)}
                                    />
                                  </td>

                                  <td className="px-5 py-4">
                                    <span className="rounded-xl bg-slate-200 px-2.5 py-1 font-mono text-[11.5px] font-black text-slate-600">
                                      #{user.account_number}
                                    </span>
                                  </td>

                                  <td className="px-5 py-4 text-[13px] font-medium text-slate-600">
                                    {user.phone_number || "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
                        {planUsers.map((user) => {
                          const initials = getInitials(user.username);
                          const palette = avatarColor(user.username);

                          return (
                            <div
                              key={user.id}
                              className="flex items-start gap-3 px-4 py-4"
                            >
                              <div
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-black ${palette}`}
                              >
                                {initials}
                              </div>

                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] font-black text-slate-900">
                                  {user.username}
                                </p>

                                <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                                  {user.email || "—"}
                                </p>

                                <p className="mt-1 font-mono text-[11px] font-bold text-slate-400">
                                  #{user.account_number}
                                </p>
                              </div>

                              <StatusBadge active={Boolean(user.status)} />
                            </div>
                          );
                        })}
                      </div>

                      {usersTotalPages > 1 && (
                        <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-100/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-[12.5px] font-medium text-slate-500">
                            Showing page{" "}
                            <span className="font-black text-slate-800">
                              {usersPage}
                            </span>{" "}
                            of{" "}
                            <span className="font-black text-slate-800">
                              {usersTotalPages}
                            </span>
                          </p>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleUsersPageChange("prev")}
                              disabled={usersPage === 1}
                              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </button>

                            <button
                              type="button"
                              onClick={() => handleUsersPageChange("next")}
                              disabled={usersPage === usersTotalPages}
                              className={`${btnBase} border border-slate-300 bg-slate-50 px-4 py-2 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl shadow-slate-950/30">
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

            <div className="flex items-start justify-between border-b border-slate-200 bg-slate-100/80 px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <Package className="h-5 w-5" />
                </div>

                <div>
                  <h2 className="text-[17px] font-black text-slate-900">
                    {editingPlan ? "Edit Plan" : "Create New Plan"}
                  </h2>

                  <p className="mt-0.5 text-[12.5px] font-medium text-slate-500">
                    {radiusPlanForm
                      ? "Radius router selected. Only billing and rate-limit fields are needed."
                      : editingPlan
                        ? "Update plan details and sync changes to MikroTik."
                        : "Add a new internet plan and sync it to MikroTik."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeDrawer}
                disabled={saving}
                className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="max-h-[calc(92vh-86px)] overflow-y-auto"
            >
              <div className="space-y-5 p-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field
                    label="Router"
                    required={!editingPlan}
                    span2
                    hint={
                      editingPlan
                        ? "Router is fixed for existing plans. Create a new plan if you need a different router."
                        : radiusPlanForm
                          ? "Radius router selected. MikroTik profile fields are hidden."
                          : "API router selected. MikroTik profile fields are available."
                    }
                  >
                    <div className="relative">
                      <Server className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                      <select
                        value={formData.router_id}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            router_id: e.target.value,
                            mikrotik_profile_id: "",
                            local_address: "",
                            remote_pool: "",
                            dns_servers: "",
                          })
                        }
                        disabled={
                          Boolean(editingPlan) || uniqueRouters.length === 0
                        }
                        className={`${inputCls} cursor-pointer appearance-none pl-10 pr-28 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`}
                        required={!editingPlan}
                      >
                        <option value="">Select router</option>

                        {uniqueRouters.map((routerOption) => (
                          <option
                            key={String(routerOption.id)}
                            value={String(routerOption.id)}
                          >
                            {routerOption.name}
                          </option>
                        ))}
                      </select>

                      <span
                        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                          radiusPlanForm
                            ? "border-violet-200 bg-violet-100 text-violet-700"
                            : "border-emerald-200 bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {radiusPlanForm ? "Radius" : "API"}
                      </span>
                    </div>
                  </Field>

                  <Field label="Plan Name" required span2>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g. Premium 50Mbps"
                      className={inputCls}
                      required
                    />
                  </Field>

                  <Field
                    label="Speed / Rate Limit"
                    required
                    hint="Example: 50M/50M or 10M/10M"
                  >
                    <input
                      type="text"
                      value={formData.speed}
                      onChange={(e) =>
                        setFormData({ ...formData, speed: e.target.value })
                      }
                      placeholder="e.g. 50M/50M"
                      className={inputCls}
                      required
                    />
                  </Field>

                  <Field label="Price (KES)" required>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder="e.g. 2500"
                      className={inputCls}
                      required
                    />
                  </Field>

                  {radiusPlanForm ? (
                    <div className="col-span-2"></div>
                  ) : (
                    <>
                      <Field
                        label="MikroTik Profile ID"
                        hint="Optional. Leave empty when creating a new profile from the API flow."
                        span2
                      >
                        <input
                          type="text"
                          value={formData.mikrotik_profile_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              mikrotik_profile_id: e.target.value,
                            })
                          }
                          placeholder="e.g. *12 or existing profile id"
                          className={inputCls}
                        />
                      </Field>

                      <Field label="Local Address">
                        <input
                          type="text"
                          value={formData.local_address}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              local_address: e.target.value,
                            })
                          }
                          placeholder="e.g. 10.0.0.1"
                          className={inputCls}
                        />
                      </Field>

                      <Field label="Remote Pool">
                        <input
                          type="text"
                          value={formData.remote_pool}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              remote_pool: e.target.value,
                            })
                          }
                          placeholder="e.g. dhcp_pool"
                          className={inputCls}
                        />
                      </Field>

                      <Field label="DNS Servers" span2>
                        <input
                          type="text"
                          value={formData.dns_servers}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              dns_servers: e.target.value,
                            })
                          }
                          placeholder="e.g. 8.8.8.8,8.8.4.4"
                          className={inputCls}
                        />
                      </Field>
                    </>
                  )}

                  <Field label="Description" span2>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Optional description for this plan"
                      className={textareaCls}
                    />
                  </Field>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2.5 border-t border-slate-200 bg-slate-100/80 px-6 py-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeDrawer}
                  disabled={saving}
                  className={`${btnBase} border border-slate-300 bg-slate-50 px-5 py-2.5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className={`${btnBase} bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-2.5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      {editingPlan ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      {editingPlan ? "Update Plan" : "Create Plan"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansPage;
